/**
 * Profile data-access layer — the read side maps the shared `myMemberInfo` query to the profile
 * domain shape; the write side wraps `updateMyMemberInfo`.
 */

import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import { ClientError } from 'graphql-request';
import type { Gender, PreferredContactMethod, UpdateMyMemberInfoInput } from '@/graphql/generated/graphql';
import { UpdateMyMemberInfoDocument } from '@/graphql/generated/graphql';
import type { MyMemberInfo } from '@/hooks/member/memberInfoApi';
import type { ProfileValidationIssue } from '@/lib/profile/profile.types';

export interface ProfilePostalAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
}

export interface MemberProfile {
  // Member Information — firstName/lastName are locked (display only, never editable; see
  // ProfileDetailsCard's static name block).
  firstName: string | null;
  lastName: string | null;
  salutation: string | null;
  dateOfBirth: string | null;
  gender: Gender | null;
  memberSince: string | null;
  memberStatus: string | null;
  mdvipId: string | null;
  // Read-only SAS URL for the member's uploaded photo; null when none is set (ProfileAvatar
  // falls back to initials).
  photoUrl: string | null;
  // Contact
  email: string | null;
  phone: string | null;
  mobilePhone: string | null;
  preferredContactMethod: PreferredContactMethod | null;
  // Address
  mailingAddress: ProfilePostalAddress | null;
  otherAddress: ProfilePostalAddress | null;
  // Insurance & Employment
  primaryInsurance: string | null;
  secondaryInsurance: string | null;
  employer: string | null;
  jobTitle: string | null;
}

/**
 * Salesforce text fields commonly come back as `""` rather than `null` for a genuinely blank
 * value. Normalize here, at the data-access boundary, so every downstream display fallback
 * (`value ?? notOnFile`) only has one "missing" representation to check.
 */
function emptyToNull(value: string | null | undefined): string | null {
  return value ? value : null;
}

function mapAddress(address: NonNullable<MyMemberInfo>['mailingAddress']): ProfilePostalAddress | null {
  if (!address) {
    return null;
  }
  return {
    line1: emptyToNull(address.line1),
    line2: emptyToNull(address.line2),
    city: emptyToNull(address.city),
    state: emptyToNull(address.state),
    zip: emptyToNull(address.zip),
    country: emptyToNull(address.country),
  };
}

export function mapToProfile(member: MyMemberInfo): MemberProfile {
  return {
    firstName: emptyToNull(member?.firstName),
    lastName: emptyToNull(member?.lastName),
    salutation: emptyToNull(member?.salutation),
    dateOfBirth: emptyToNull(member?.dateOfBirth),
    gender: member?.gender ?? null,
    memberSince: emptyToNull(member?.memberSince),
    memberStatus: emptyToNull(member?.memberStatus),
    mdvipId: emptyToNull(member?.mdvipId),
    photoUrl: member?.profilePhotoUrl?.url ?? null,
    email: emptyToNull(member?.email),
    phone: emptyToNull(member?.phone),
    mobilePhone: emptyToNull(member?.mobilePhone),
    preferredContactMethod: member?.preferredContactMethod ?? null,
    mailingAddress: mapAddress(member?.mailingAddress),
    otherAddress: mapAddress(member?.otherAddress),
    primaryInsurance: emptyToNull(member?.primaryInsurance),
    secondaryInsurance: emptyToNull(member?.secondaryInsurance),
    employer: emptyToNull(member?.employer),
    jobTitle: emptyToNull(member?.jobTitle),
  };
}

// ── Write side ─────────────────────────────────────────

/**
 * The mutation's own return shape — a deliberate subset of `MyMemberInfo` (see
 * `updateMyMemberInfo.graphql`): every profile field, but not the Payments-only ones
 * (`autoPay`, `currentBalanceFormula`, ...) this dialog never touches. `useUpdateMyProfile`
 * merges this into the existing cached `MyMemberInfo` rather than replacing it wholesale, so
 * those omitted fields survive the update untouched.
 */
export type UpdatedMemberInfo = Awaited<ReturnType<typeof requestUpdateMyMemberInfo>>;

/** One backend validation failure, surfaced as `updateMyMemberInfo`'s `BAD_REQUEST` errors-envelope entry. */
export class UpdateMyMemberInfoError extends Error {
  constructor(public readonly issues: ProfileValidationIssue[]) {
    super('BAD_REQUEST');
    this.name = 'UpdateMyMemberInfoError';
  }
}

/**
 * `BAD_REQUEST` is the only failure mode `updateMyMemberInfo` expresses with a structured body
 * (per the API's `ZodValidationPipe` — see its header comment for the exact wire shape). Anything
 * else (session expired, Salesforce unreachable, ...) has no `issues[]` and is left for the
 * caller's `extractGqlErrorCode` + toast path instead.
 */
function extractValidationIssues(error: unknown): ProfileValidationIssue[] | null {
  if (!(error instanceof ClientError)) return null;
  const extensions = error.response.errors?.[0]?.extensions;
  if (extensions?.code !== 'BAD_REQUEST' || !Array.isArray(extensions.issues) || extensions.issues.length === 0) {
    return null;
  }
  return extensions.issues as ProfileValidationIssue[];
}

export async function requestUpdateMyMemberInfo(input: UpdateMyMemberInfoInput) {
  try {
    const data = await gqlRequest(UpdateMyMemberInfoDocument, { input });
    return data.updateMyMemberInfo.member;
  } catch (error) {
    const issues = extractValidationIssues(error);
    if (issues) {
      throw new UpdateMyMemberInfoError(issues);
    }
    throw error;
  }
}
